<template>
  <div class="claim-trial-root">
    <transition name="trial-banner" mode="out-in">
      <div
        v-if="showClaimBanner"
        key="claim"
        class="claim-trial-banner"
        role="banner"
      >
        <div class="claim-trial-banner__inner">
          <div class="claim-trial-banner__text">
            <p class="claim-trial-banner__desc">
              领取后立享
              <span class="claim-trial-banner__highlight">{{ trialDays }}</span>
              天 免费试用
            </p>
          </div>
          <button
            type="button"
            class="claim-trial-banner__btn"
            :disabled="trialClaiming"
            @click="openTrialApplyDialog"
          >
            立即领取
          </button>
        </div>
      </div>
      <div
        v-else-if="showStatusBanner"
        key="status"
        class="trial-status-banner"
        role="banner"
      >
        <div class="trial-status-banner__inner">
          <div class="trial-status-banner__center">
            <div class="trial-status-banner__countdown">
              <span class="trial-status-banner__unit-group">
                <span class="trial-status-banner__box">{{ countdown.days }}</span>
                <span class="trial-status-banner__unit">天</span>
              </span>
              <span class="trial-status-banner__unit-group">
                <span class="trial-status-banner__box">{{
                  countdown.hours
                }}</span>
                <span class="trial-status-banner__unit">时</span>
              </span>
              <span class="trial-status-banner__unit-group">
                <span class="trial-status-banner__box">{{
                  countdown.minutes
                }}</span>
                <span class="trial-status-banner__unit">分</span>
              </span>
              <span class="trial-status-banner__unit-group">
                <span class="trial-status-banner__box">{{
                  countdown.seconds
                }}</span>
                <span class="trial-status-banner__unit">秒</span>
              </span>
            </div>
          </div>
          <div class="trial-status-banner__action">
            <button
              type="button"
              class="claim-trial-banner__btn"
              @click="$emit('purchase')"
            >
              购买服务
            </button>
            <p
              v-if="showRealnameHint"
              class="trial-status-banner__tip"
              role="button"
              tabindex="0"
              @click="openRealnameDialog"
              @keydown.enter.prevent="openRealnameDialog"
            >
              购买正式服务前请先进行实名认证
            </p>
          </div>
        </div>
      </div>
    </transition>

    <TrialAgreementDialog
      :visible.sync="showTrialAgreementDialog"
      :submitting="trialClaiming"
      @agree="onTrialAgreementAgree"
    />

    <TrialSuccessDialog
      :visible.sync="showTrialSuccessDialog"
      :trial-days="successTrialDays"
      @confirm="onTrialSuccessConfirm"
      @verify="onTrialSuccessOpenRealname"
      @purchase="onTrialSuccessPurchase"
    />

    <RealnameDialog
      :visible.sync="showRealnameDialog"
      @success="onRealnameSuccess"
    />
  </div>
</template>

<script>
import realnameDialogMixin from "../realname/realnameDialogMixin";
import subscriptionTrialApplyMixin from "./subscriptionTrialApplyMixin";
import {
  shouldShowRealnameHint,
} from "../realname/realnameService";
import {
  subscriptionState,
  parseTrialEndTimestamp,
} from "./subscriptionService";

export default {
  name: "ClaimTrial",

  mixins: [realnameDialogMixin, subscriptionTrialApplyMixin],

  data() {
    return {
      countdown: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      },
      countdownTimer: null,
    };
  },

  computed: {
    /** 当前订阅状态 */
    status() {
      return subscriptionState.status;
    },
    /** 试用结束时间字符串 */
    trialEndTime() {
      return (this.status && this.status.trialEndTime) || "";
    },
    /** 是否显示「立即领取」横幅 */
    showClaimBanner() {
      return subscriptionState.loaded && subscriptionState.view.claim;
    },
    /** 是否显示试用倒计时横幅（含试用到期倒计时为 0） */
    showStatusBanner() {
      return subscriptionState.loaded && subscriptionState.view.trialBanner;
    },
    /** 是否应运行倒计时（横幅展示且存在试用结束时间） */
    shouldRunCountdown() {
      return this.showStatusBanner && !!this.trialEndTime;
    },
    /** 可领取的试用天数 */
    trialDays() {
      return subscriptionState.trialDays || 14;
    },
    /** 试用结束时间戳（毫秒） */
    trialEndTimestamp() {
      return parseTrialEndTimestamp(this.trialEndTime);
    },
    /** 是否展示「请先实名」提示 */
    showRealnameHint() {
      return shouldShowRealnameHint();
    },
  },

  watch: {
    shouldRunCountdown: {
      immediate: true,
      handler(active) {
        if (active) {
          this.startCountdown();
        } else {
          this.stopCountdown();
        }
      },
    },
    trialEndTime() {
      if (this.shouldRunCountdown) {
        this.tickCountdown();
      }
    },
  },

  /** 组件销毁前清除倒计时 */
  beforeDestroy() {
    this.stopCountdown();
  },

  methods: {
    /** 试用领取成功，通知父组件刷新 */
    onTrialApplied() {
      this.$emit("claimed");
    },

    /** 数字补零为两位字符串 */
    padTwo(num) {
      return String(num).padStart(2, "0");
    },
    /** 根据剩余时间刷新倒计时显示 */
    tickCountdown() {
      const diff = Math.max(0, this.trialEndTimestamp - Date.now());
      this.countdown = {
        days: this.padTwo(Math.floor(diff / 86400000)),
        hours: this.padTwo(Math.floor((diff % 86400000) / 3600000)),
        minutes: this.padTwo(Math.floor((diff % 3600000) / 60000)),
        seconds: this.padTwo(Math.floor((diff % 60000) / 1000)),
      };
    },
    /** 启动每秒刷新倒计时 */
    startCountdown() {
      this.stopCountdown();
      this.tickCountdown();
      this.countdownTimer = setInterval(() => {
        this.tickCountdown();
      }, 1000);
    },
    /** 清除倒计时定时器 */
    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
  },
};
</script>

<style scoped>
.claim-trial-root {
  position: relative;
}

.trial-banner-enter-active,
.trial-banner-leave-active {
  transform-origin: top center;
  transition:
    opacity 0.5s ease,
    transform 0.5s ease,
    max-height 0.5s ease,
    margin-bottom 0.5s ease;
  overflow: hidden;
}

.trial-banner-enter,
.trial-banner-leave-to {
  opacity: 0;
  transform: translateY(-16px);
  max-height: 0;
  margin-bottom: 0;
}

.trial-banner-enter-to,
.trial-banner-leave {
  opacity: 1;
  transform: translateY(0);
  max-height: 140px;
}

.claim-trial-banner {
  box-sizing: border-box;
  margin-bottom: 16px;
}

.claim-trial-banner__inner {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  min-height: 120px;
  margin: -8px -20px;
  padding: 12px 40px 12px 20px;
  border-radius: 8px;
  background: url("./img/bg1.png") no-repeat center / cover;
  box-shadow: 0 2px 8px rgba(122, 79, 42, 0.08);
}

.claim-trial-banner__text {
  flex: 1;
  min-width: 0;
}

.claim-trial-banner__desc {
  margin: 25px 180px 0;
  font-size: 14px;
  color: #322b26;
  line-height: 1.5;
}

.claim-trial-banner__highlight {
  margin: 0 2px;
  font-size: 16px;
  font-weight: 700;
  color: #e8432a;
}

.claim-trial-banner__btn {
  flex-shrink: 0;
  min-width: 160px;
  height: 48px;
  padding: 0 24px;
  font-size: 16px;
  font-weight: 500;
  color: #4d2508;
  background: linear-gradient(-90deg, #ffe5b5 0%, #ffead1 100%);
  border: none;
  border-radius: 999px;
  box-shadow: 0px 4px 8px 0px rgba(163, 120, 78, 0.2);
  cursor: pointer;
}

.claim-trial-banner__btn:hover:not(:disabled) {
  background: #ffbc6d;
}

.claim-trial-banner__btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.trial-status-banner {
  box-sizing: border-box;
  margin-bottom: 16px;
}

.trial-status-banner__inner {
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-items: center;
  min-height: 120px;
  margin: -8px -20px;
  padding: 12px 40px 12px 20px;
  border-radius: 8px;
  background: url("./img/bg2.png") no-repeat center / cover;
  box-shadow: 0 2px 8px rgba(122, 79, 42, 0.08);
}

.trial-status-banner__center {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -58%);
  display: flex;
  align-items: center;
  justify-content: center;
}


.trial-status-banner__countdown {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.trial-status-banner__unit-group {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.trial-status-banner__box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 45px;
  height: 50px;
  padding: 0 6px;
  font-size: 30px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(0deg, #df9c53, #e6b47c);
  box-shadow: 0px -1px 0px 0px #fffdfa, 0px 1px 0px 0px #a46f36;
  border-radius: 4px;
  text-shadow: 0px 1px 0px #A26A20;
}

.trial-status-banner__unit {
  font-size: 14px;
  color: #4a4038;
}

.trial-status-banner__action {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  margin-left: auto;
}

.trial-status-banner__tip {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  font-size: 12px;
  color: #e8633a;
  line-height: 1.4;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}

.trial-status-banner__tip:hover {
  text-decoration: underline;
}
</style>



// WEBPACK FOOTER //
// src/view/user/subscriptionService/claimTrial.vue